import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../config/supabase";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorMessage from "../components/ErrorMessage";

interface WeekMenuData {
  [day: number]: {
    meal: string;
    snack: string;
  };
}

const AdminPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [weekNumber, setWeekNumber] = useState<number>(1);
  const [weekData, setWeekData] = useState<WeekMenuData>({});
  const [rotationStartDate, setRotationStartDate] = useState<string>("");
  const [snacksData, setSnacksData] = useState<{ [day: number]: string }>({});

  const daysOfWeek = [
    { id: 1, name: "Lunedı̀" },
    { id: 2, name: "Martedì" },
    { id: 3, name: "Mercoledì" },
    { id: 4, name: "Giovedì" },
    { id: 5, name: "Venerdı̀" },
  ];

  useEffect(() => {
    const initializeData = async () => {
      await loadRotationStartDate();
      await loadWeekData();
      await loadSnacksData();
    };
    initializeData();
  }, [weekNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRotationStartDate = async () => {
    try {
      const { data, error } = await supabase
        .from("settings")
        .select("rotation_start_date")
        .single();

      if (!error && data) {
        setRotationStartDate(data.rotation_start_date);
      }
    } catch (err) {
      console.error("Errore nel caricamento della data di rotazione:", err);
    }
  };

  const loadWeekData = async () => {
    try {
      const { data, error } = await supabase
        .from("menus")
        .select("day_of_week, meal")
        .eq("week_number", weekNumber);

      if (!error && data) {
        const weekTemp: WeekMenuData = {};
        // Inizializza tutti i giorni
        daysOfWeek.forEach((day) => {
          weekTemp[day.id] = { meal: "", snack: "" };
        });

        data.forEach((item: any) => {
          if (weekTemp[item.day_of_week]) {
            weekTemp[item.day_of_week] = {
              ...weekTemp[item.day_of_week],
              meal: item.meal || "",
            };
          }
        });
        setWeekData(weekTemp);
      } else {
        // Inizializza con valori vuoti se non ci sono dati
        const weekTemp: WeekMenuData = {};
        daysOfWeek.forEach((day) => {
          weekTemp[day.id] = { meal: "", snack: "" };
        });
        setWeekData(weekTemp);
      }
    } catch (err) {
      console.error("Errore nel caricamento dei dati della settimana:", err);
      // Inizializza con valori vuoti in caso di errore
      const weekTemp: WeekMenuData = {};
      daysOfWeek.forEach((day) => {
        weekTemp[day.id] = { meal: "", snack: "" };
      });
      setWeekData(weekTemp);
    } finally {
      setLoading(false);
    }
  };

  const loadSnacksData = async () => {
    try {
      const { data, error } = await supabase
        .from("snacks")
        .select("day_of_week, snack");

      if (!error && data) {
        const snacksTemp: { [day: number]: string } = {};
        data.forEach((item: any) => {
          snacksTemp[item.day_of_week] = item.snack || "";
        });
        setSnacksData(snacksTemp);

        // Sincronizza con weekData
        setWeekData((prev) => {
          const updated = { ...prev };
          daysOfWeek.forEach((day) => {
            updated[day.id] = {
              ...(updated[day.id] || { meal: "", snack: "" }),
              snack: snacksTemp[day.id] || "",
            };
          });
          return updated;
        });
      }
    } catch (err) {
      console.error("Errore nel caricamento delle merende:", err);
    }
  };

  const handleMealChange = (day: number, value: string) => {
    setWeekData((prev) => ({
      ...prev,
      [day]: {
        ...(prev[day] || { meal: "", snack: "" }),
        meal: value,
      },
    }));
  };

  const handleSnackChange = (day: number, value: string) => {
    setSnacksData((prev) => ({
      ...prev,
      [day]: value,
    }));
    setWeekData((prev) => ({
      ...prev,
      [day]: {
        ...(prev[day] || { meal: "", snack: "" }),
        snack: value,
      },
    }));
  };

  const handleRotationStartDateChange = (value: string) => {
    setRotationStartDate(value);
  };

  const saveWeekMenus = async () => {
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      // Salva i pasti della settimana
      for (const [dayString, dayData] of Object.entries(weekData)) {
        const day = parseInt(dayString);
        if (dayData.meal) {
          // Controlla se esiste già un menù per questo giorno e settimana
          const { data: existing } = await supabase
            .from("menus")
            .select("id")
            .eq("week_number", weekNumber)
            .eq("day_of_week", day)
            .single();

          if (existing) {
            // Aggiorna esistente
            await supabase
              .from("menus")
              .update({ meal: dayData.meal })
              .eq("week_number", weekNumber)
              .eq("day_of_week", day);
          } else {
            // Inserisci nuovo
            await supabase.from("menus").insert({
              week_number: weekNumber,
              day_of_week: day,
              meal: dayData.meal,
            });
          }
        }
      }

      // Salva le merende
      for (const [dayString, snack] of Object.entries(snacksData)) {
        const day = parseInt(dayString);
        if (snack) {
          const { data: existing } = await supabase
            .from("snacks")
            .select("id")
            .eq("day_of_week", day)
            .single();

          if (existing) {
            await supabase
              .from("snacks")
              .update({ snack })
              .eq("day_of_week", day);
          } else {
            await supabase.from("snacks").insert({
              day_of_week: day,
              snack,
            });
          }
        }
      }

      setSuccess("Menù salvato con successo!");
    } catch (err) {
      setError("Errore nel salvataggio dei dati");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const saveRotationDate = async () => {
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const { data: existing } = await supabase
        .from("settings")
        .select("id")
        .single();

      if (existing) {
        await supabase
          .from("settings")
          .update({ rotation_start_date: rotationStartDate })
          .eq("id", existing.id);
      } else {
        await supabase
          .from("settings")
          .insert({ rotation_start_date: rotationStartDate });
      }

      setSuccess("Data di rotazione salvata con successo!");
    } catch (err) {
      setError("Errore nel salvataggio della data di rotazione");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
  };

  if (loading) {
    return <LoadingSpinner message="Caricamento interfaccia admin..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-soft-pink via-soft-yellow to-soft-blue">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Pannello Admin - Mensa Nido
            </h1>
            <Link
              to="/"
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              ← Torna alla homepage
            </Link>
          </div>
          <button
            onClick={logout}
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Esci
          </button>
        </div>

        {error && <ErrorMessage message={error} />}
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-6">
            {success}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Configurazione rotazione */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-blue-100">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-2xl">⚙️</span>
              </div>
              <h2 className="text-xl font-semibold text-gray-800">
                Configurazione Rotazione
              </h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data inizio rotazione
                </label>
                <input
                  type="date"
                  value={rotationStartDate}
                  onChange={(e) =>
                    handleRotationStartDateChange(e.target.value)
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button
                onClick={saveRotationDate}
                disabled={saving}
                className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white font-bold py-3 px-6 rounded-lg transition-colors"
              >
                {saving ? "Salvando..." : "Salva Data Rotazione"}
              </button>
            </div>
          </div>

          {/* Selezione settimana */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-green-100">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-2xl">📅</span>
              </div>
              <h2 className="text-xl font-semibold text-gray-800">
                Gestione Settimane
              </h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Settimana da modificare
                </label>
                <select
                  value={weekNumber}
                  onChange={(e) => setWeekNumber(parseInt(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value={1}>Settimana 1</option>
                  <option value={2}>Settimana 2</option>
                  <option value={3}>Settimana 3</option>
                  <option value={4}>Settimana 4</option>
                </select>
              </div>
              <p className="text-sm text-gray-600">
                La rotazione si ripete ciclicamente ogni 4 settimane
              </p>
            </div>
          </div>
        </div>

        {/* Menu della settimana */}
        <div className="mt-8 bg-white rounded-2xl shadow-lg p-6 border-2 border-pink-100">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mr-4">
              <span className="text-2xl">🍽️</span>
            </div>
            <h2 className="text-xl font-semibold text-gray-800">
              Menù Settimana {weekNumber}
            </h2>
          </div>

          <div className="space-y-6">
            {daysOfWeek.map((day) => (
              <div
                key={day.id}
                className="border-b border-gray-200 pb-6 last:border-b-0"
              >
                <h3 className="text-lg font-medium text-gray-800 mb-4">
                  {day.name}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pranzo
                    </label>
                    <textarea
                      value={weekData[day.id]?.meal || ""}
                      onChange={(e) => handleMealChange(day.id, e.target.value)}
                      placeholder="Es:&#10;Pasta al pomodoro&#10;Pollo arrosto&#10;Patate arrosto&#10;Frutta di stagione"
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-y"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Merenda
                    </label>
                    <textarea
                      value={snacksData[day.id] || ""}
                      onChange={(e) =>
                        handleSnackChange(day.id, e.target.value)
                      }
                      placeholder="Es:&#10;Biscotti&#10;Succo di frutta"
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-y"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex justify-end">
            <button
              onClick={saveWeekMenus}
              disabled={saving}
              className="bg-pink-500 hover:bg-pink-600 disabled:bg-pink-300 text-white font-bold py-3 px-8 rounded-lg transition-colors text-lg"
            >
              {saving ? "Salvando..." : "Salva Menù"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
